import { useEffect, useState } from 'react'

import { useMembers, useSendWelcome, useTrip, useUpdateTrip } from '../../hooks/queries'
import { SendIcon } from '../../components/Icons'
import { useTextgrenzen } from '../../hooks/useTextgrenzen'

/**
 * Die Willkommensnachricht einer Reise (#36).
 *
 * Aus dem Betrieb: „Text mit %N, ohne Fotos." `%N` steht für den Namen, **den
 * sich der Empfänger bei der Anmeldung selbst gegeben hat** — nie für den
 * Zweitnamen, den die Verwaltung einem Konto geben kann. Ersetzt wird er auf
 * dem Server, je Empfänger; hier steht nur eine Vorschau.
 *
 * **Verschickt wird sie als gewöhnliche private Nachricht** des Reisenden.
 * Damit trägt sie den gewohnten Push-Weg, die Häkchen und den Verlauf — ein
 * zweiter Nachrichtentyp daneben wäre eine zweite Mechanik für dieselbe
 * Sache.
 *
 * **Ohne Text steht der Knopf nicht da.** Eine leere Willkommensnachricht ist
 * keine, und ein Knopf, der dann eine Fehlermeldung bringt, ist ein kaputter.
 */
export function Willkommenskarte({ tripId }: { tripId: number }) {
  const grenzen = useTextgrenzen()
  const trip = useTrip(tripId)
  const members = useMembers(tripId, true)
  const speichern = useUpdateTrip(tripId)
  const senden = useSendWelcome(tripId)

  const hinterlegt = trip.data?.welcome_text ?? ''
  const [text, setText] = useState(hinterlegt)
  const [gemerkt, setGemerkt] = useState(false)

  // Was der Server hat, gewinnt — aber nur, solange hier nichts Eigenes
  // steht: sonst überschriebe eine Antwort von unterwegs den halben Satz,
  // den gerade jemand tippt.
  useEffect(() => {
    setText((bisher) => (bisher === '' ? hinterlegt : bisher))
  }, [hinterlegt])

  const empfaenger = (members.data ?? []).filter(
    (m) => m.role === 'recipient' && m.left !== true && !m.disabled && !m.cut_off,
  )
  const offene = empfaenger.filter((m) => m.welcomed !== true)
  // Ein Name aus der Runde für die Vorschau. Steht noch niemand da, tut es
  // ein Beispielname — eine Vorschau mit `%N` darin wäre keine.
  const beispiel = empfaenger[0]?.user.display_name ?? 'Anna'
  const vorschau = text.replace(/%N/g, beispiel)
  const geaendert = text.trim() !== hinterlegt.trim()

  return (
    <div className="card card--seite">
      <h2 className="card__titel">Willkommensnachricht</h2>
      <p className="card__lead">
        Der Gruß an alle, die du dieser Reise neu zuordnest. Er geht als private
        Nachricht raus – nur an den Einzelnen, mit Benachrichtigung und Häkchen
        wie jede andere.
      </p>

      <label className="field">
        <span className="field__label">Text</span>
        <textarea
          className="field__input"
          rows={4}
          maxLength={grenzen.trip_description}
          value={text}
          placeholder="Hallo %N, schön, dass du mitliest!"
          onChange={(event) => {
            setText(event.target.value)
            setGemerkt(false)
          }}
        />
        <span className="feld__hinweis feld__hinweis--13">
          <strong>%N</strong> wird durch den Namen ersetzt, den sich die Person
          bei der Anmeldung selbst gegeben hat. Fotos gehen nicht mit.
        </span>
      </label>

      {text.trim() !== '' && (
        <p className="feld__hinweis feld__hinweis--13">
          So sieht sie bei <strong>{beispiel}</strong> aus:
          <br />
          <span style={{ color: 'var(--text)' }}>{vorschau}</span>
        </p>
      )}

      {speichern.isError && (
        <p className="notice" role="alert">
          {speichern.error.message}
        </p>
      )}
      {senden.isError && (
        <p className="notice" role="alert">
          {senden.error.message}
        </p>
      )}

      <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
        <button
          type="button"
          className="button button--small"
          disabled={!geaendert || speichern.isPending}
          onClick={() => {
            speichern.mutate(
              { welcome_text: text.trim() === '' ? null : text.trim() },
              { onSuccess: () => setGemerkt(true) },
            )
          }}
        >
          {speichern.isPending ? 'Wird gespeichert …' : 'Text speichern'}
        </button>

        {/* Erst wenn ein Text **hinterlegt** ist, nicht schon beim Tippen:
            verschickt würde sonst der gespeicherte und nicht der sichtbare. */}
        {hinterlegt.trim() !== '' && (
          <button
            type="button"
            className="button button--outline button--small"
            disabled={senden.isPending || offene.length === 0 || geaendert}
            onClick={() => senden.mutate(undefined)}
          >
            <SendIcon size={15} />
            {/* **Eine Aussage über das Verschicken, nicht über die
                Empfänger.** Aus dem Betrieb: „Knopf ‚alle haben sie‘ →
                ‚alle versendet‘." Vor einem Knopf fragt man sich, was er
                tut und ob es schon getan ist. */}
            {offene.length === 0
              ? 'Alle versendet'
              : `An alle senden, die sie noch nicht haben (${offene.length})`}
          </button>
        )}
      </div>

      {gemerkt && !geaendert && (
        <p className="feld__hinweis feld__hinweis--13" role="status">
          Gespeichert.
        </p>
      )}
      {senden.isSuccess && !senden.isPending && (
        <p className="feld__hinweis feld__hinweis--13" role="status">
          {senden.data.sent === 0
            ? 'Es gab niemanden, der sie noch nicht hatte.'
            : `Verschickt: ${senden.data.sent}.`}
        </p>
      )}
      {geaendert && hinterlegt.trim() !== '' && (
        <p className="feld__hinweis feld__hinweis--13">
          Erst speichern – verschickt wird der hinterlegte Text.
        </p>
      )}
    </div>
  )
}
